/**
 * View: Merchant Approval Detail
 * Displays detailed information about a merchant submission for approval/rejection
 */

import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/modal";
import { ConfirmationModal } from "@/components/ui/confirmation-modal";
import { SuccessModal } from "@/components/ui/success-modal";
import type { MerchantApprovalDetail } from "../../domain/model/MerchantApprovalModel";
import {
  ApproveMerchantUseCase,
  FetchMerchantApprovalDetailUseCase,
  RejectMerchantUseCase,
} from "../../domain/usecase/MerchantApprovalUseCases";
import { formatDateTime } from "@/utils/Format";

export const MerchantApprovalDetailView = (): React.JSX.Element => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [merchantDetail, setMerchantDetail] = useState<MerchantApprovalDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<{ url: string; title: string } | null>(null);
  const isPreviewOpen = !!previewImage;

  // Confirmation modal states
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectReasonError, setRejectReasonError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Success modal states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  usePageTitle("Merchant Approval Detail");

  useEffect(() => {
    const loadMerchantDetail = async () => {
      if (!id) {
        setError("Merchant ID is missing.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const detail = await FetchMerchantApprovalDetailUseCase(id);
        setMerchantDetail(detail);
        setError(null);
      } catch (loadError) {
        console.error("Failed to load merchant detail:", loadError);
        setMerchantDetail(null);
        setError("Failed to load merchant details. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    void loadMerchantDetail();
  }, [id]);

  const handleApprove = () => {
    setIsApproveModalOpen(true);
  };

  const handleReject = () => {
    setRejectReason("");
    setRejectReasonError(null);
    setIsRejectModalOpen(true);
  };

  const handleApproveConfirm = async () => {
    if (!merchantDetail) {
      return;
    }

    setIsProcessing(true);

    try {
      await ApproveMerchantUseCase(merchantDetail.merchantId ?? "");
      setMerchantDetail({
        ...merchantDetail,
        approvalStatus: "APPROVED",
      });
      setIsApproveModalOpen(false);
      setSuccessMessage("Merchant has been approved successfully.");
      setShowSuccessModal(true);
    } catch (approveError) {
      console.error("Failed to approve merchant:", approveError);
      setError("Failed to approve merchant. Please try again.");
      setIsApproveModalOpen(false);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRejectConfirm = async () => {
    if (!merchantDetail) {
      return;
    }

    if (!rejectReason.trim()) {
      setRejectReasonError("Rejection reason is required.");
      return;
    }

    setIsProcessing(true);

    try {
      await RejectMerchantUseCase(merchantDetail.merchantId ?? "", rejectReason.trim());
      setMerchantDetail({
        ...merchantDetail,
        approvalStatus: "REJECTED",
      });
      setIsRejectModalOpen(false);
      setSuccessMessage("Merchant has been rejected successfully.");
      setShowSuccessModal(true);
    } catch (rejectError) {
      console.error("Failed to reject merchant:", rejectError);
      setError("Failed to reject merchant. Please try again.");
      setIsRejectModalOpen(false);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApproveCancel = () => {
    setIsApproveModalOpen(false);
  };

  const handleRejectCancel = () => {
    setRejectReasonError(null);
    setIsRejectModalOpen(false);
  };

  const handleGoBack = () => {
    navigate("/swarapoint/merchant-approval");
  };

  if (isLoading) {
    return (
      <MainLayout title="Merchant Approval Detail" onBack={handleGoBack}>
        <div className="flex items-center justify-center py-12">
          <p className="text-slate-500">Loading merchant details...</p>
        </div>
      </MainLayout>
    );
  }

  if (!merchantDetail) {
    return (
      <MainLayout title="Merchant Approval Detail" onBack={handleGoBack}>
        <div className="flex items-center justify-center py-12">
          <p className="text-slate-500">
            {error || "Merchant submission not found."}
          </p>
        </div>
      </MainLayout>
    );
  }

  const merchantId = merchantDetail.merchantId;
  const isPending = (merchantDetail.approvalStatus ?? "").toUpperCase() === "PENDING";

  return (
    <MainLayout
      title={`Merchant Submission Details - #${merchantId}`}
      onBack={handleGoBack}
    >
      <div className="flex flex-col gap-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Informasi Identitas Bisnis Section */}
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
          <h3 className="text-sm font-semibold text-[#1F2937] mb-4 text-left">
            Informasi Identitas Bisnis
          </h3>
          <div className="grid grid-cols-3 gap-x-8 gap-y-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-[#6B7280] text-left">
                Nama Merchant
              </label>
              <p className="text-sm font-medium text-[#111827] text-left">{merchantDetail.businessProfile?.merchantName || merchantDetail.merchantName || "-"}</p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-[#6B7280] text-left">
                Jenis Usaha
              </label>
              <p className="text-sm font-medium text-[#111827] text-left">{merchantDetail.businessProfile?.merchantType || merchantDetail.merchantType || "-"}</p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-[#6B7280] text-left">
                Jam Operasional
              </label>
              <p className="text-sm font-medium text-[#111827] text-left">{merchantDetail.businessProfile?.operationalHours || "-"}</p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-[#6B7280] text-left">
                Alamat lengkap
              </label>
              <p className="text-sm font-medium text-[#111827] text-left">{merchantDetail.businessProfile?.address || "-"}</p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-[#6B7280] text-left">
                Deskripsi Singkat
              </label>
              <p className="text-sm font-medium text-[#111827] text-left">
                {merchantDetail.businessProfile?.description || "-"}
              </p>
            </div>
          </div>
        </div>

        {/* Data Pemilik / Penanggung Jawab Section */}
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
          <h3 className="text-sm font-semibold text-[#1F2937] mb-4 text-left">
            Data Pemilik / Penanggung Jawab
          </h3>
          <div className="grid grid-cols-3 gap-x-8 gap-y-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-[#6B7280] text-left">
                Nama Lengkap Pemilik
              </label>
              <p className="text-sm font-medium text-[#111827] text-left">{merchantDetail.ownerProfile?.ownerName || "-"}</p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-[#6B7280] text-left">
                Nomor Kontak
              </label>
              <p className="text-sm font-medium text-[#111827] text-left">{merchantDetail.ownerProfile?.ownerContact || "-"}</p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-[#6B7280] text-left">
                Email
              </label>
              <p className="text-sm font-medium text-[#111827] text-left">{merchantDetail.ownerProfile?.ownerEmail || "-"}</p>
            </div>
            <div className="flex flex-col gap-1 col-span-3">
              <label className="text-xs text-[#6B7280] text-left">
                Foto Identitas
              </label>
              <div className="mt-2">
                {merchantDetail.ownerProfile?.ownerIdPhotoUrl ? (
                  <img
                    src={merchantDetail.ownerProfile.ownerIdPhotoUrl}
                    alt="ID Card"
                    className="w-[200px] h-auto rounded border border-[#E5E7EB] cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => setPreviewImage({
                      url: merchantDetail.ownerProfile?.ownerIdPhotoUrl ?? "",
                      title: "Foto Identitas",
                    })}
                  />
                ) : (
                  <p className="text-sm text-[#6B7280]">-</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Data Rekening Bank Section */}
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
          <h3 className="text-sm font-semibold text-[#1F2937] mb-4 text-left">
            Data Rekening Bank
          </h3>
          <div className="grid grid-cols-3 gap-x-8 gap-y-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-[#6B7280] text-left">
                Nama Bank
              </label>
              <p className="text-sm font-medium text-[#111827] text-left">{merchantDetail.bankAccount?.bankName || "-"}</p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-[#6B7280] text-left">
                Nomor Rekening
              </label>
              <p className="text-sm font-medium text-[#111827] text-left">{merchantDetail.bankAccount?.accountNumber || "-"}</p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-[#6B7280] text-left">
                Nama Pemilik Rekening
              </label>
              <p className="text-sm font-medium text-[#111827] text-left">{merchantDetail.bankAccount?.accountHolderName || "-"}</p>
            </div>
          </div>
        </div>

        {/* Dokumen Tambahan Section */}
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
          <h3 className="text-sm font-semibold text-[#1F2937] mb-4 text-left">
            Dokumen Tambahan
          </h3>
          <div className="grid grid-cols-3 gap-x-8 gap-y-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-[#6B7280] text-left">
                NPWP
              </label>
              <p className="text-sm font-medium text-[#111827] text-left">{merchantDetail.legalDocuments?.npwpNumber || "-"}</p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-[#6B7280] text-left">
                Nomor Izin Usaha
              </label>
              <p className="text-sm font-medium text-[#111827] text-left">{merchantDetail.legalDocuments?.businessLicenseNumber || "-"}</p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-[#6B7280] text-left">
                Foto Lokasi Usaha
              </label>
              <div className="mt-2">
                {merchantDetail.legalDocuments?.locationPhotoUrl ? (
                  <img
                    src={merchantDetail.legalDocuments.locationPhotoUrl}
                    alt="Business Location"
                    className="w-[200px] h-auto rounded border border-[#E5E7EB] cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => setPreviewImage({
                      url: merchantDetail.legalDocuments?.locationPhotoUrl ?? "",
                      title: "Foto Lokasi Usaha",
                    })}
                  />
                ) : (
                  <p className="text-sm text-[#6B7280]">-</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
          <h3 className="text-sm font-semibold text-[#1F2937] mb-4 text-left">
            Status Approval
          </h3>
          <div className="grid grid-cols-3 gap-x-8 gap-y-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-[#6B7280] text-left">
                Approval Status
              </label>
              <p className="text-sm font-medium text-[#111827] text-left">{merchantDetail.approvalStatus || "-"}</p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-[#6B7280] text-left">
                Active Status
              </label>
              <p className="text-sm font-medium text-[#111827] text-left">{merchantDetail.isActive ? "Active" : "Inactive"}</p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-[#6B7280] text-left">
                Submitted At
              </label>
              <p className="text-sm font-medium text-[#111827] text-left">{merchantDetail.createdAt ? formatDateTime(merchantDetail.createdAt) : "-"}</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        {isPending && (
          <div className="flex justify-end gap-3 mt-2">
            <Button
              variant="outline"
              size="md"
              onClick={handleReject}
              className="!border-red-600 !text-red-600 hover:!bg-red-50"
            >
              Reject
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={handleApprove}
            >
              Approve
            </Button>
          </div>
        )}
      </div>

      {/* Image Preview Modal */}
      <Modal isOpen={isPreviewOpen} onClose={() => setPreviewImage(null)}>
        <div className="flex flex-col gap-4">
          {previewImage && (
            <>
              <h3 className="text-lg font-semibold text-[#1F2937] text-left">
                {previewImage.title}
              </h3>
              <div className="flex items-center justify-center">
                <img
                  src={previewImage.url}
                  alt={previewImage.title}
                  className="max-w-[70vw] max-h-[70vh] object-contain rounded-lg"
                />
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Reject Modal */}
      <Modal isOpen={isRejectModalOpen} onClose={handleRejectCancel}>
        <div className="rounded-2xl w-[460px] max-w-[90vw] relative">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-3">
            Reject Merchant
          </h2>
          <p className="text-center text-gray-600 mb-6">
            Provide a reason for rejecting this merchant submission.
          </p>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rejection Reason
            </label>
            <textarea
              value={rejectReason}
              onChange={(event) => {
                setRejectReason(event.target.value);
                if (rejectReasonError) {
                  setRejectReasonError(null);
                }
              }}
              rows={4}
              placeholder="Example: Documents are incomplete or unreadable"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-400"
            />
            {rejectReasonError && (
              <p className="mt-2 text-sm text-red-600">{rejectReasonError}</p>
            )}
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleRejectCancel}
              disabled={isProcessing}
              className="flex-1 py-3 px-6 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                void handleRejectConfirm();
              }}
              disabled={isProcessing}
              className="flex-1 py-3 px-6 text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {isProcessing ? "Processing..." : "Reject"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Approve Confirmation Modal */}
      <ConfirmationModal
        isOpen={isApproveModalOpen}
        onClose={handleApproveCancel}
        onConfirm={() => {
          void handleApproveConfirm();
        }}
        title="Approve Merchant"
        message="Are you sure you want to approve this merchant submission?"
        confirmText="Approve"
        cancelText="Cancel"
        isLoading={isProcessing}
        variant="default"
      />

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          navigate("/swarapoint/merchant-approval");
        }}
        title="Success"
        message={successMessage}
      />
    </MainLayout>
  );
};

